import React from 'react'
import classNames from 'classnames'

import { withStyles, createStyles, WithStyles, Theme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import Paper from 'components/Paper'

import { FormattedMessage } from 'react-intl'

interface Props {
  totalBudget: number
  remainingBudget: number
  hasTooManyGoalkeepers: boolean
}

const styles = (theme: Theme) => createStyles({
  root: {
    ...theme.components.BudgetIndicator.default.root,
    height: 28,
    position: 'relative',
  },
  rootError: {
    ...theme.components.BudgetIndicator.error.root,
  },
  progress: {
    ...theme.components.BudgetIndicator.default.progress,
    top: 0,
    left: 0,
    bottom: 0,
    position: 'absolute',
  },
  progressError: {
    ...theme.components.BudgetIndicator.error.progress,
  },
  progressTransition: {
    transitionProperty: 'width background-color',
    transitionDuration: `${theme.transitions.duration.standard}ms`,
  },
  rounded: {
    borderRadius: theme.shape.borderRadius,
  },
  roundedLeft: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  textContainer: {
    zIndex: 1,
    height: '100%',
    display: 'flex',
    position: 'relative',
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  text: {
    ...theme.components.BudgetIndicator.default.text,
  },
  textError: {
    ...theme.components.BudgetIndicator.error.text,
  },
  budgetValue: {
    marginLeft: 3,
    marginRight: 3
  }
})

type InjectedProps = Props & WithStyles<typeof styles>

const BudgetIndicator: React.SFC<InjectedProps> = ({
  totalBudget,
  remainingBudget,
  hasTooManyGoalkeepers,
  classes,
}) => {
  const insufficientFunds = remainingBudget < 0
  const exactlyZeroLeftover = remainingBudget === 0
  const usedPercent = Math.max(
    1, remainingBudget > 0
      ? Math.round(((remainingBudget) / totalBudget) * 100)
      : 100
  )

  const hasError = insufficientFunds || hasTooManyGoalkeepers

  const rootClass = classNames(classes.root, classes.rounded, {
    [classes.rootError]: hasError,
  })

  const progressClass = classNames(classes.progress, classes.rounded, {
    [classes.roundedLeft]: usedPercent < 100,
    [classes.progressTransition]: !hasError,
    [classes.progressError]: exactlyZeroLeftover,
  })

  const textClass = classNames(classes.text, {
    [classes.textError]: hasError,
  })

  return (
    <Paper color="accent" className={rootClass}>
      <div className={progressClass} style={{ width: `${hasError ? 0 : usedPercent}%` }} />
      <div className={classes.textContainer}>
        <Typography className={textClass} color="inherit" variant="caption" noWrap>
          {hasTooManyGoalkeepers
            ? <FormattedMessage id="teameditor_error_overgoalkeeperlimit" />
            : (
              <>
                <span className={classes.budgetValue}>{remainingBudget}</span>
                <FormattedMessage id="teameditor_remaining_budget" />
              </>
            )}
        </Typography>
      </div>
    </Paper>
  )
}

export default withStyles(styles)(BudgetIndicator)
